// Samtycke för Google Ads via Consent Mode. index.html sätter allt till
// 'denied' och köar gtag-anropen; här avgörs om och när gtag.js laddas.
export const CONSENT_KEY = 'bd_consent';

// false = Consent Mode "basic": gtag.js laddas först efter Godkänn.
// true = "advanced": gtag.js laddas med denied och skickar cookielösa pingar
// före samtycke. Ger fler modellerade konverteringar men är juridiskt mer osäkert.
export const LOAD_GTAG_BEFORE_CONSENT = false;

const VERSION = 1;
const MAX_AGE_DAYS = 365;
const ADS_ID = 'AW-17796287850';
const OPEN_EVENT = 'bd:consent-open';
// Bannern ber bara om att få mäta annonser. Personalisering (remarketing) förblir
// nekad även efter Godkänn, så att samtycket inte omfattar mer än besökaren fått veta.
const TYPES_GRANTED_ON_ACCEPT = ['ad_storage', 'ad_user_data', 'analytics_storage'];
const ALL_TYPES = ['ad_storage', 'ad_user_data', 'ad_personalization', 'analytics_storage'];
const DAY_MS = 24 * 60 * 60 * 1000;

// Används bara när localStorage inte går att använda – då gäller valet
// tills sidan laddas om.
let memoryConsent = null;
let storageWriteFailed = false;
// null = standardläget från index.html (allt denied)
let appliedAds = null;
let scriptRequested = false;
let scriptLoaded = false;

function gtag() {
  window.dataLayer = window.dataLayer || [];
  // gtag.js tolkar bara arguments-objekt som kommandon, inte vanliga arrayer
  window.dataLayer.push(arguments);
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function parseConsent(value) {
  if (!value || value.v !== VERSION || typeof value.ads !== 'boolean') return null;
  if (typeof value.date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value.date)) return null;
  const ageDays = (Date.parse(today()) - Date.parse(value.date)) / DAY_MS;
  // Ett datum långt fram i tiden skulle annars aldrig gå ut
  if (!(ageDays >= -1 && ageDays <= MAX_AGE_DAYS)) return null;
  return { ads: value.ads, date: value.date };
}

// available: false betyder att localStorage är blockerat
function readStoredConsent() {
  let raw;
  try {
    raw = localStorage.getItem(CONSENT_KEY);
  } catch {
    return { available: false, consent: null };
  }
  try {
    return { available: true, consent: raw ? parseConsent(JSON.parse(raw)) : null };
  } catch {
    return { available: true, consent: null };
  }
}

// Det sparade valet går före minnet, så att ett val som gjorts i en annan
// flik gäller direkt även här.
export function getConsent() {
  const stored = readStoredConsent();
  return stored.available && !storageWriteFailed ? stored.consent : parseConsent(memoryConsent);
}

export function hasAdsConsent() {
  return getConsent()?.ads === true;
}

export function setConsent({ ads }) {
  memoryConsent = { v: VERSION, ads: ads === true, date: today() };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(memoryConsent));
    storageWriteFailed = false;
  } catch {
    // Valet gäller bara i minnet
    storageWriteFailed = true;
  }
  applyConsent();
}

export function applyConsent() {
  if (typeof window === 'undefined' || window.__PRERENDER__) return;

  const ads = hasAdsConsent();
  if (ads !== appliedAds) {
    if (ads) {
      updateGoogleConsent(TYPES_GRANTED_ON_ACCEPT, 'granted');
    } else if (appliedAds) {
      updateGoogleConsent(ALL_TYPES, 'denied');
    }
    appliedAds = ads;
    syncLoadedFlag();
  }
  // Varje gång utan samtycke, inte bara när det återkallas: det tar även bort
  // lagring som den gamla taggen satte före samtycket eller som en annan flik
  // hann skriva.
  if (!ads) clearAdsStorage();

  const path = window.location.pathname;
  if (path.startsWith('/portal') || path.startsWith('/admin')) return;
  if (ads || LOAD_GTAG_BEFORE_CONSENT) loadGtag();
}

export function openConsentSettings() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

export function onConsentOpen(handler) {
  window.addEventListener(OPEN_EVENT, handler);
  return () => window.removeEventListener(OPEN_EVENT, handler);
}

// Ett val eller en rensning i en annan flik ska gälla direkt även i den här
export function onConsentChange(handler) {
  const listener = (event) => {
    if (event.key !== CONSENT_KEY && event.key !== null) return;
    applyConsent();
    handler(getConsent());
  };
  window.addEventListener('storage', listener);
  return () => window.removeEventListener('storage', listener);
}

function updateGoogleConsent(types, state) {
  gtag('consent', 'update', Object.fromEntries(types.map((type) => [type, state])));
}

// gtag_report_conversion i index.html skickar bara när skriptet är laddat och
// samtycket fortfarande gäller.
function syncLoadedFlag() {
  if (!scriptLoaded) return;
  window.__bdAdsLoaded = LOAD_GTAG_BEFORE_CONSENT || appliedAds === true;
}

function loadGtag() {
  if (scriptRequested) return;
  scriptRequested = true;
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${ADS_ID}`;
  script.onload = () => {
    scriptLoaded = true;
    syncLoadedFlag();
  };
  document.head.appendChild(script);
}

function clearAdsStorage() {
  try {
    const domain = window.location.hostname.replace(/^www\./, '');
    document.cookie.split(';').forEach((pair) => {
      const name = pair.split('=')[0].trim();
      if (!name.startsWith('_gcl_')) return;
      const expired = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      document.cookie = expired;
      document.cookie = `${expired}; domain=.${domain}`;
    });
  } catch { /* ignore */ }

  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('_gcl_')) keys.push(key);
    }
    keys.forEach((key) => localStorage.removeItem(key));
  } catch { /* ignore */ }
}

if (typeof window !== 'undefined') {
  // Läses av gtag_report_conversion i index.html vid varje konvertering
  window.__bdHasAdsConsent = hasAdsConsent;
}
